import React, {useState} from 'react'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Stack,
    Flex,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useGiftCardFields from '../../../components/forms/useGiftCardCheckBalanceFields'
import Field from '../../../components/field'
import {useForm} from 'react-hook-form'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import GoogleRecatpchaText from '../../../components/google-recatpcha-text'
import {sendViewErrorEvent} from '../../../utils/gtm-utils'
import WebDrawer from '../../../components/web-drawer'
import {useBrand} from '../../../contexts/brand-context'

export const GiftCardCheckBalance = ({isOpen, onClose}) => {
    const {isGarage} = useBrand()
    const {formatMessage} = useIntl()
    const {app: appConfig} = getConfig()
    const form = useForm()
    const fields = useGiftCardFields({form})
    const {currency} = useCurrency()
    const [balance, setBalance] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const {getTokenWhenReady} = useAccessToken()
    const {executeRecaptcha} = useGoogleReCaptcha()
    const proxyPath = appConfig.commerceAPI.proxyPath
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId

    const onSubmit = async (data) => {
        setIsLoading(true)
        const {cardNumber, pin} = data

        try {
            // Get reCAPTCHA token
            let recaptchaToken = null
            if (executeRecaptcha) {
                try {
                    recaptchaToken = await executeRecaptcha('gc_balance')
                } catch (error) {
                    console.error('reCAPTCHA error:', error)
                }
            } else {
                console.warn('reCAPTCHA not available - proceeding without verification')
            }

            const token = await getTokenWhenReady()
            const response = await fetch(
                `${proxyPath}/custom/smartclixx/v1/organizations/${organizationId}/check-balance?siteId=${siteId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        cardNumber,
                        pin,
                        ...(recaptchaToken && {recaptchaToken})
                    })
                }
            )

            const res = await response.json()

            if (response.ok && res.success) {
                setBalance(res.amount)
            } else {
                const errorMessage = res.message || 'Failed to check balance.'
                let gtmMsg = errorMessage

                if (errorMessage.startsWith('DECLINE Inv Card')) {
                    gtmMsg = 'Please enter a valid gift card number'
                    form.setError('cardNumber', {
                        type: 'manual',
                        message: formatMessage({
                            id: 'checkout_gc.invalid',
                            defaultMessage: 'Please enter a valid gift card number'
                        })
                    })
                } else if (errorMessage.startsWith('DECLINE Inv CVV2')) {
                    gtmMsg = 'Enter a valid gift card pin'
                    form.setError('pin', {
                        type: 'manual',
                        message: isGarage
                            ? formatMessage({
                                  id: 'checkout_gc.invalid_card_pin.garage',
                                  defaultMessage: 'Enter a valid gift card pin'
                              })
                            : formatMessage({
                                  id: 'checkout_gc.invalid_card_pin.dynamite',
                                  defaultMessage: 'Enter a valid gift card pin'
                              })
                    })
                } else {
                    form.setError('cardNumber', {
                        type: 'manual',
                        message: errorMessage
                    })
                }
                // GTM Modification
                sendViewErrorEvent(gtmMsg)

                setBalance(0)
            }

            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            form.setError('cardNumber', {
                type: 'manual',
                message: 'An unexpected error occurred. Please try again.'
            })
        }
    }

    return (
        <>
            <WebDrawer
                isOpen={isOpen}
                onClose={onClose}
                size="85vh"
                title={
                    <Text as="span" textStyle="webH3">
                        <FormattedMessage
                            id="gift_card.check_balance.title"
                            defaultMessage="Check Balance"
                        />
                    </Text>
                }
            >
                <Box>
                    <form id="gift-card-check-balance" onSubmit={form.handleSubmit(onSubmit)}>
                        <Stack spacing={8}>
                            <Text textStyle={'bodySmRegular'}>
                                {isGarage ? (
                                    <FormattedMessage
                                        id="gift_card.check_balance.description.garage"
                                        defaultMessage="Checking your Gift Card balance is easy. Simply enter your number and PIN."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="gift_card.check_balance.description.dynamite"
                                        defaultMessage="Checking your Gift Card balance is easy. Simply enter your number and PIN."
                                    />
                                )}
                            </Text>

                            {/* Form fields */}
                            <Box>
                                <Field {...fields.cardNumber} />
                                <Field {...fields.pin} formControlProps={{mt: 5}} />
                            </Box>

                            {/* Your Balance */}
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                bg="webTheme.backgroundPrimaryBrand"
                                p={{base: '12px 16px'}}
                                flexDirection={{base: 'column'}}
                            >
                                <Text textStyle={'bodyXsSRegular'} mb={2}>
                                    {isGarage ? (
                                        <FormattedMessage
                                            id="gift_card.check_balance.your_balance.garage"
                                            defaultMessage="Your Balance"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="gift_card.check_balance.your_balance.dynamite"
                                            defaultMessage="Your Balance"
                                        />
                                    )}
                                </Text>
                                <Text textStyle={'webH1'}>
                                    <FormattedNumber
                                        value={balance}
                                        style="currency"
                                        currency={currency}
                                    />
                                </Text>
                            </Flex>

                            {/* Submit and Recaptcha text */}
                            <Stack spacing={4}>
                                <Button
                                    type="submit"
                                    form="gift-card-check-balance"
                                    isLoading={isLoading}
                                    loadingText={formatMessage({
                                        id: 'gift_card.check_balance.loading',
                                        defaultMessage: 'Checking Balance...'
                                    })}
                                    variant="webPrimary"
                                    mt={0}
                                >
                                    <FormattedMessage
                                        id="gift_card.check_balance.button"
                                        defaultMessage="Check Balance"
                                    />
                                </Button>
                                <GoogleRecatpchaText />
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </WebDrawer>
        </>
    )
}
